import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from "recharts";
import { Package, AlertTriangle, Clock, TrendingDown, Layers, DollarSign, ArrowUpDown, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { API_URL as API } from "@/lib/api";
import TCBadge from "@/components/TCBadge";

const CHART_COLORS = ["#ef4444", "#f59e0b", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];
const URGENCY_CONFIG = {
  critico: { label: "Critico", color: "bg-red-100 text-red-800 border-red-300", bar: "#ef4444" },
  alto: { label: "Alto", color: "bg-amber-100 text-amber-800 border-amber-300", bar: "#f59e0b" },
  medio: { label: "Medio", color: "bg-blue-100 text-blue-800 border-blue-300", bar: "#3b82f6" },
  bajo: { label: "Bajo", color: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "#10b981" },
};

const formatBs = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Bs';
const formatUSD = (v) => '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const LoteAnalysis = ({ tipoCambio }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "lote", direction: "asc" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/lotes/analysis`);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching lotes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = useMemo(() => {
    if (!data?.lotes) return [];
    const items = [...data.lotes];
    if (sortConfig.key) {
      items.sort((a, b) => {
        const av = a[sortConfig.key] ?? 0;
        const bv = b[sortConfig.key] ?? 0;
        return sortConfig.direction === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
    }
    return items;
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };

  const urgencyCounts = useMemo(() => {
    if (!data?.lotes) return {};
    const counts = { critico: 0, alto: 0, medio: 0, bajo: 0 };
    data.lotes.forEach(l => { counts[l.urgency] = (counts[l.urgency] || 0) + 1; });
    return counts;
  }, [data]);

  const chartData = useMemo(() => {
    if (!data?.lotes) return [];
    return data.lotes.map(l => ({
      name: `L${l.lote}`,
      stock: l.total_stock,
      valor: Math.round(l.valor_pmenor_bs),
      urgency: l.urgency,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="lote-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF9900]" />
      </div>
    );
  }

  if (!data) return <p className="text-center text-muted-foreground py-10">Error cargando datos de lotes</p>;

  return (
    <div className="space-y-4" data-testid="lote-analysis">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="heading-font text-lg font-bold text-foreground flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#FF9900]" /> Analisis de Lotes — FIFO
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Prioridad de salida basada en antiguedad y stock</p>
        </div>
        <div className="flex items-center gap-2">
          {tipoCambio && <TCBadge tc={tipoCambio} />}
          <Button variant="outline" size="sm" onClick={fetchData} className="text-xs gap-1.5" data-testid="lote-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="lote-kpis">
        <Card className="p-3 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase">Total Lotes</p>
          <p className="text-xl font-bold text-foreground">{data.total_lotes}</p>
        </Card>
        <Card className="p-3 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase">Unidades Stock</p>
          <p className="text-xl font-bold text-foreground">{data.total_stock?.toLocaleString()}</p>
        </Card>
        <Card className="p-3 border border-emerald-200 bg-emerald-50/50">
          <p className="text-[10px] text-emerald-700 uppercase flex items-center gap-1"><DollarSign className="h-3 w-3" />Valor PMenor</p>
          <p className="text-sm font-bold text-emerald-800">{formatBs(data.total_valor_bs)}</p>
          <p className="text-[10px] text-emerald-600">{formatUSD(data.total_valor_usd)}</p>
        </Card>
        {Object.entries(urgencyCounts).map(([key, count]) => (
          count > 0 && (
            <Card key={key} className={`p-3 border ${URGENCY_CONFIG[key].color}`}>
              <p className="text-[10px] uppercase flex items-center gap-1">
                {key === 'critico' ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                {URGENCY_CONFIG[key].label}
              </p>
              <p className="text-xl font-bold">{count} lotes</p>
            </Card>
          )
        ))}
      </div>

      {/* Chart: Stock por Lote */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 border border-border" data-testid="lote-chart-stock">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-1.5">
            <Package className="h-4 w-4 text-[#FF9900]" /> Stock por Lote
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9 }} />
              <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v) => v.toLocaleString()} />
              <Bar dataKey="stock" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={URGENCY_CONFIG[entry.urgency]?.bar || "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 border border-border" data-testid="lote-chart-urgency">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-1.5">
            <TrendingDown className="h-4 w-4 text-[#FF9900]" /> Urgencia FIFO
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={Object.entries(urgencyCounts).filter(([, c]) => c > 0).map(([k, c]) => ({ name: URGENCY_CONFIG[k].label, value: c }))}
                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false} style={{ fontSize: 10 }}>
                {Object.entries(urgencyCounts).filter(([, c]) => c > 0).map(([k], i) => (
                  <Cell key={i} fill={URGENCY_CONFIG[k]?.bar || "#94a3b8"} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" height={30} iconSize={10} wrapperStyle={{ fontSize: 10 }} />
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Lotes Table */}
      <Card className="border border-border overflow-hidden" data-testid="lote-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/80">
              <TableRow>
                {[
                  { key: "lote", label: "Lote" },
                  { key: "year", label: "Año" },
                  { key: "age_years", label: "Antiguedad" },
                  { key: "urgency", label: "Urgencia" },
                  { key: "sku_count", label: "SKUs" },
                  { key: "total_stock", label: "Stock" },
                  { key: "sin_stock", label: "Sin Stock" },
                  { key: "valor_pmenor_bs", label: "Valor PMenor" },
                  { key: "valor_pmenor_usd", label: "Valor USD" },
                  { key: "pct_stock", label: "% Stock" },
                  { key: "pct_valor", label: "% Valor" },
                ].map(col => (
                  <TableHead key={col.key}>
                    <Button variant="ghost" size="sm" onClick={() => handleSort(col.key)} className="text-[11px] px-1 h-7 font-semibold">
                      {col.label} <ArrowUpDown className="ml-1 h-2.5 w-2.5" />
                    </Button>
                  </TableHead>
                ))}
                <TableHead className="text-[11px] font-semibold px-2">Familias</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((l, i) => {
                const urg = URGENCY_CONFIG[l.urgency] || URGENCY_CONFIG.bajo;
                return (
                  <TableRow key={i} className="hover:bg-muted/50" data-testid={`lote-row-${l.lote}`}>
                    <TableCell className="mono-font font-bold text-sm">{l.lote}</TableCell>
                    <TableCell className="mono-font text-xs">{l.year || '-'}</TableCell>
                    <TableCell className="mono-font text-xs">{l.age_years > 0 ? `${l.age_years} año${l.age_years > 1 ? 's' : ''}` : 'Actual'}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] border ${urg.color}`}>{urg.label}</Badge>
                    </TableCell>
                    <TableCell className="mono-font text-xs">{l.sku_count}</TableCell>
                    <TableCell className="mono-font text-xs font-bold">{l.total_stock?.toLocaleString()}</TableCell>
                    <TableCell className="mono-font text-xs">{l.sin_stock > 0 ? <span className="text-red-600 font-bold">{l.sin_stock}</span> : '0'}</TableCell>
                    <TableCell className="mono-font text-xs">{formatBs(l.valor_pmenor_bs)}</TableCell>
                    <TableCell className="mono-font text-xs text-[#FF9900]">{formatUSD(l.valor_pmenor_usd)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(l.pct_stock, 100)}%`, backgroundColor: urg.bar }} />
                        </div>
                        <span className="mono-font text-[10px]">{l.pct_stock}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(l.pct_valor, 100)}%` }} />
                        </div>
                        <span className="mono-font text-[10px]">{l.pct_valor}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[10px] text-muted-foreground max-w-[160px] truncate">{l.familias?.join(', ')}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* FIFO Priority Products */}
      {data.fifo_priority_products?.length > 0 && (
        <Card className="p-4 border-2 border-red-200 bg-red-50/30" data-testid="fifo-products">
          <h3 className="text-sm font-bold text-red-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Productos Prioridad FIFO — Salida Urgente
          </h3>
          <p className="text-xs text-red-600 mb-3">Productos de lotes antiguos con mayor stock. Priorizar su salida para evitar obsolescencia.</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-red-100/50">
                <TableRow>
                  <TableHead className="text-[11px] font-semibold">Codigo</TableHead>
                  <TableHead className="text-[11px] font-semibold">Descripcion</TableHead>
                  <TableHead className="text-[11px] font-semibold">Lote</TableHead>
                  <TableHead className="text-[11px] font-semibold">Familia</TableHead>
                  <TableHead className="text-[11px] font-semibold">Stock</TableHead>
                  <TableHead className="text-[11px] font-semibold">PMenor</TableHead>
                  <TableHead className="text-[11px] font-semibold">PMayor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.fifo_priority_products.map((p, i) => (
                  <TableRow key={i} className="hover:bg-red-50" data-testid={`fifo-row-${i}`}>
                    <TableCell className="mono-font text-xs font-bold">{p.codigo}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{p.descripcion}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] border-red-300 text-red-700">{p.lote}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.familia}</TableCell>
                    <TableCell className="mono-font text-xs font-bold text-red-700">{(p.stock || 0).toLocaleString()}</TableCell>
                    <TableCell className="mono-font text-xs">{formatBs(p.pmenor || 0)}</TableCell>
                    <TableCell className="mono-font text-xs">{formatBs(p.pmayor || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default LoteAnalysis;
